import { useEffect, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { WS_BASE_URL } from '../config.js';
import { LOGOUT_EVENT, performLogout } from '../utils/auth';
import { ensureSessionHealthy } from '../utils/sessionManager.js';
import { isNetworkRelatedError } from '../api/api.js';
import { showWarning } from '../utils/toast.js';

const MAX_RETRIES = 5;
const BASE_RECONNECT_DELAY = 1000;
const LONG_RECONNECT_DELAY = 30000;
const RECONNECT_JITTER_RATIO = 0.2;
const SESSION_EXPIRED_MESSAGE = 'Sessão expirada, faça login novamente.';
// Janela inicial em que há maior chance de rajada de eventos após conectar
const SUPPRESS_MS_AFTER_CONNECT = 2000;
// Intervalo de coalescência padrão para reduzir tempestade de callbacks
const DEFAULT_THROTTLE_MS = 250;

const applyReconnectJitter = (delay) => {
  const safeDelay = Number.isFinite(delay) && delay > 0 ? delay : BASE_RECONNECT_DELAY;
  const jitterWindow = Math.max(250, Math.floor(safeDelay * RECONNECT_JITTER_RATIO));
  return safeDelay + Math.floor(Math.random() * jitterWindow);
};

function useAttendantWebSocket(attendantId, companyId, onMessageReceived, setIsConnected) {
  const wsRef = useRef(null);
  const retryCountRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const clientIdRef = useRef(uuidv4());
  const isMountedRef = useRef(false);
  const connectedAtRef = useRef(0);
  const loggedOutRef = useRef(false);
  const connectRef = useRef(null);
  const scheduleReconnectRef = useRef(null);
  const sessionHealthWarningRef = useRef(false);

  const onMessageRef = useRef(onMessageReceived);
  const setIsConnectedRef = useRef(setIsConnected);
  // Buffer/Throttle
  const messageQueueRef = useRef([]);
  const flushTimerRef = useRef(null);

  useEffect(() => {
    onMessageRef.current = onMessageReceived;
    setIsConnectedRef.current = setIsConnected;
  }, [onMessageReceived, setIsConnected]);

  const disconnect = useCallback(() => {
    loggedOutRef.current = false;
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;

      if ([WebSocket.OPEN, WebSocket.CONNECTING].includes(wsRef.current.readyState)) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    messageQueueRef.current = [];
    setIsConnectedRef.current?.(false);
  }, []);

  const ensureSessionBeforeConnect = useCallback(async () => {
    const health = await ensureSessionHealthy();
    if (health.allowed) {
      sessionHealthWarningRef.current = false;
      return health;
    }
    if (!sessionHealthWarningRef.current) {
      sessionHealthWarningRef.current = true;
      showWarning(SESSION_EXPIRED_MESSAGE);
    }
    return health;
  }, []);

  const connect = useCallback(
    async ({ skipHealthCheck = false } = {}) => {
      if (!isMountedRef.current || loggedOutRef.current) return;

      if (!attendantId || !companyId) {
        disconnect();
        setIsConnectedRef.current?.(false);
        return;
      }

      disconnect();

      if (!skipHealthCheck) {
        const health = await ensureSessionBeforeConnect();
        if (!health.allowed) {
          return;
        }
      }

      if (!isMountedRef.current || loggedOutRef.current) {
        return;
      }

      const wsUrl = `${WS_BASE_URL}/ws/${companyId}/${attendantId}/${clientIdRef.current}`;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          connectedAtRef.current = Date.now(); // marca o início da conexão
          retryCountRef.current = 0;
          setIsConnectedRef.current?.(true);
        };
        ws.onmessage = (event) => {
          const { data: rawData } = event;
          let parsed;
          try {
            parsed = JSON.parse(rawData);
          } catch {
            if (typeof rawData === 'string' && rawData.trim()) {
              parsed = { type: 'raw_text_message', raw: rawData };
            } else {
              parsed = rawData;
            }
          }

          // Empilha e agenda flush com coalescência
          messageQueueRef.current.push(parsed);

          if (flushTimerRef.current) return;

          const sinceConnect = Date.now() - (connectedAtRef.current || 0);
          const throttle =
            sinceConnect < SUPPRESS_MS_AFTER_CONNECT
              ? Math.max(DEFAULT_THROTTLE_MS, 500)
              : DEFAULT_THROTTLE_MS;

          flushTimerRef.current = setTimeout(() => {
            const batch = messageQueueRef.current.splice(0);
            flushTimerRef.current = null;
            // Entrega em lote, preservando ordem
            const cb = onMessageRef.current;
            if (typeof cb === 'function') {
              for (const msg of batch) cb(msg);
            }
          }, throttle);
        };

        ws.onerror = (error) => {
          console.error('❌ Erro no WebSocket:', error);
          setIsConnectedRef.current?.(false);
          ws.close();
        };

        ws.onclose = (event) => {
          const { code, reason } = event;

          // Backend pode encerrar explicitamente com motivo "logout" ou códigos de auth.
          const reasonStr = (reason || '').toString().toLowerCase();
          const forcedLogout =
            reasonStr.includes('logout') ||
            code === 4003 ||
            code === 4403 ||
            code === 4401;
          if (forcedLogout) {
            loggedOutRef.current = true;
            setIsConnectedRef.current?.(false);
            performLogout();
            return;
          }

          if (!isMountedRef.current || loggedOutRef.current) {
            return;
          }

          setIsConnectedRef.current?.(false);
          const log = [1000, 1001].includes(code) ? console.info : console.warn;
          log(`WebSocket fechado (Código: ${code}, Razão: ${reason || 'N/A'})`);

          const delay = Math.min(30000, BASE_RECONNECT_DELAY * Math.pow(2, retryCountRef.current));
          if (retryCountRef.current < MAX_RETRIES) {
            console.warn(
              `Tentando reconectar em ${delay / 1000}s (Tentativa ${retryCountRef.current + 1}/${MAX_RETRIES})`
            );
            scheduleReconnectRef.current?.(delay, code);
          } else {
            console.warn('🛑 Número máximo de tentativas de reconexão atingido. Nova tentativa em instantes.');
            scheduleReconnectRef.current?.(LONG_RECONNECT_DELAY, code, {
              resetRetryCountAfter: true,
            });
          }
        };
      } catch (error) {
        console.error('Erro ao criar WebSocket:', error);
        disconnect();
      }
    },
    [attendantId, companyId, disconnect, ensureSessionBeforeConnect]
  );
  connectRef.current = connect;

  const scheduleReconnect = useCallback(
    (delay, reasonCode, options = {}) => {
      const safeDelay = Number.isFinite(delay) && delay > 0 ? delay : BASE_RECONNECT_DELAY;
      const jitteredDelay = applyReconnectJitter(safeDelay);
      const { resetRetryCountAfter = false, connectOptions = {} } = options;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      reconnectTimeoutRef.current = setTimeout(async () => {
        reconnectTimeoutRef.current = null;
        if (!isMountedRef.current || loggedOutRef.current) {
          return;
        }
        if (!attendantId || !companyId) {
          return;
        }

        const requiresSessionValidation = [1005, 1006].includes(reasonCode);
        if (requiresSessionValidation) {
          const health = await ensureSessionBeforeConnect();
          if (!health.allowed) {
            const shouldRetry = isNetworkRelatedError(health.reason);
            if (shouldRetry) {
              const nextDelay = Math.min(30000, jitteredDelay * 2);
              scheduleReconnect(nextDelay, reasonCode, options);
            } else {
              console.warn('Reconexão WS interrompida: sessão inválida', health.reason);
            }
            return;
          }
        }

        if (resetRetryCountAfter) {
          retryCountRef.current = 0;
        } else {
          retryCountRef.current += 1;
        }

        const skipHealthCheck = connectOptions.skipHealthCheck ?? requiresSessionValidation;
        connectRef.current?.({ ...connectOptions, skipHealthCheck });
      }, jitteredDelay);
    },
    [attendantId, companyId, ensureSessionBeforeConnect]
  );
  scheduleReconnectRef.current = scheduleReconnect;

  // monta/desmonta apenas uma vez (evita dupla conexão)
  useEffect(() => {
    isMountedRef.current = true;
    const attemptReconnect = () => {
      if (!isMountedRef.current || loggedOutRef.current) return;
      if (!attendantId || !companyId) return;
      if (
        wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }
      connectRef.current?.();
    };
    const handleLogout = () => {
      loggedOutRef.current = true;
      if (
        wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)
      ) {
        try {
          wsRef.current.close(1000, 'logout');
        } catch {
          // Socket pode ter fechado entre a checagem e o close.
        }
      }
      wsRef.current = null;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      setIsConnectedRef.current?.(false);
    };

    const handleOnline = () => {
      attemptReconnect();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        attemptReconnect();
      }
    };

    window.addEventListener(LOGOUT_EVENT, handleLogout);
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      isMountedRef.current = false;
      window.removeEventListener(LOGOUT_EVENT, handleLogout);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
      disconnect();
    };
  }, [attendantId, companyId, disconnect]);

  // reconecta quando o atendente mudar
  useEffect(() => {
    loggedOutRef.current = false;
    if (isMountedRef.current) {
      clientIdRef.current = uuidv4(); // novo ID ao mudar de atendente/empresa
      connectRef.current?.();
    }
  }, [attendantId, companyId]);

  // expõe noop para convenção de hooks (retornar nada)
  return undefined;
}

export default useAttendantWebSocket;
